import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  inviteMember,
  listInvitations,
  listMembers,
  memberResetLink,
  removeMember,
  revokeInvitation,
  setMemberRole,
  type TInvitation,
  type TInvitationCreated,
  type TRole,
} from '../../entities/organization';

interface IHandLink {
  id: string;
  email: string;
  url: string;
}

export const useMembers = () => {
  const client = useQueryClient();

  const members = useQuery({ queryKey: ['members'], queryFn: listMembers });
  const invitations = useQuery({ queryKey: ['invitations'], queryFn: listInvitations });

  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['members'] }),
      client.invalidateQueries({ queryKey: ['invitations'] }),
    ]);
  };

  const role = useMutation({
    mutationFn: ({ userId, next }: { userId: string; next: TRole }) => setMemberRole(userId, next),
    onSuccess: refresh,
  });

  const remove = useMutation({ mutationFn: removeMember, onSuccess: refresh });

  // Only set when the installation has no mail transport: the link exists nowhere else, and
  // it is lost with this screen — Send again mints a fresh one if they close it too early.
  const [handLink, setHandLink] = useState<IHandLink | null>(null);
  const created = async (invitation: TInvitationCreated) => {
    setHandLink(
      invitation.inviteUrl
        ? { id: invitation.id, email: invitation.email, url: invitation.inviteUrl }
        : null,
    );
    await refresh();
  };

  const invite = useMutation({
    mutationFn: ({ email, as }: { email: string; as: TRole }) => inviteMember(email, as),
    onSuccess: created,
  });

  const revoke = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: async (_, id) => {
      setHandLink((link) => (link?.id === id ? null : link));
      await refresh();
    },
  });

  // There is no resend route and only the link's hash is kept, so resend must revoke and re-invite.
  const resend = useMutation({
    mutationFn: async (invitation: TInvitation) => {
      await revokeInvitation(invitation.id);
      return inviteMember(invitation.email, invitation.role);
    },
    onSuccess: created,
  });

  const resetLink = useMutation({ mutationFn: memberResetLink });

  const failure =
    role.error ?? remove.error ?? invite.error ?? revoke.error ?? resend.error ?? null;

  return {
    members,
    invitations,
    role,
    remove,
    invite,
    revoke,
    resend,
    resetLink,
    handLink,
    dismissHandLink: () => setHandLink(null),
    failure,
  };
};

export type TMembers = ReturnType<typeof useMembers>;
